import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { createOneOffSession } from "./api";
import DialogHeader from "./DialogHeader";

interface CreateOneOffSessionDialogProps {
  open: boolean;
  clubId: number | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const CreateOneOffSessionDialog: React.FC<CreateOneOffSessionDialogProps> = ({
  open,
  clubId,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [name, setName] = useState("");
  const [sessionDate, setSessionDate] = useState<Dayjs | null>(null);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setSessionDate(dayjs().hour(18).minute(0).second(0).millisecond(0));
      setNotes("");
    }
  }, [open]);

  const handleCreate = async () => {
    if (clubId == null || !name.trim() || !sessionDate) return;
    try {
      setCreating(true);
      await createOneOffSession({
        club_id: clubId,
        name: name.trim(),
        session_date: sessionDate.format("DD/MM/YYYY HH:mm"),
        notes: notes.trim() || undefined,
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating one-off session:", err);
      onError("Failed to create one-off session. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title="Create One-off Session"
        onClose={onClose}
        closeDisabled={creating}
      />
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Session name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Training Day Workshop"
              variant="outlined"
              disabled={creating}
            />
            <DateTimePicker
              label="Session date & time"
              value={sessionDate}
              onChange={setSessionDate}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />
            <TextField
              fullWidth
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              variant="outlined"
              disabled={creating}
            />
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !name.trim() || !sessionDate}
          startIcon={creating ? <CircularProgress size={20} /> : <Add />}
        >
          {creating ? "Creating…" : "Create Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOneOffSessionDialog;
